import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { SelectValue } from 'antd/lib/select';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { getZonesOfPreceptorApplications } from '../../../service/ScreeningService';

const { Option } = Select;

interface OptionData {
	value: number;
	label: string;
}

interface RemoteSingleSelectProps {
	onChange?: (values: Array<OptionData>) => void;
	value?: Array<number>;
	functionarySrcmId?: string;
	regionId?: number;
	batchId?: number;
	isFilterOpen?: boolean;
}

const RemoteSingleSelect: React.FC<RemoteSingleSelectProps> = ({
	regionId,
	onChange,
	functionarySrcmId,
	batchId,
	value,
	isFilterOpen,
}: RemoteSingleSelectProps) => {
	const [options, setOptions] = useState<OptionData[]>([]);
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const [selectedValue, setSelectedValue] = useState<OptionData[]>();

	const fetchData = useCallback(
		async (searchValue: string) => {
			setLoading(true);

			if (functionarySrcmId && regionId) {
				const zonesPage = await getZonesOfPreceptorApplications({
					functionarySrcmId,
					batchId,
					regionIds: [regionId],
				});
				let filtered = zonesPage.zones;
				if (!filtered) {
					setOptions([]);
					setSelectedValue(undefined);
					setLoading(false);
					return;
				}

				if (searchValue) {
					filtered = filtered.filter((item) =>
						item.zoneName?.toLowerCase().includes(searchValue.toLowerCase())
					);
				}

				const transformedOptions: OptionData[] = filtered
					.filter(
						(item): item is { zoneId: number; zoneName: string } =>
							!!item.zoneName
					)
					.map((item) => ({
						value: item.zoneId,
						label: item.zoneName,
					}));

				if (value?.length) {
					const matchedValues = filtered
						.filter((zone) => value.includes(zone.zoneId))
						.map((zone) => ({
							value: zone.zoneId,
							label: zone.zoneName,
						}));

					setSelectedValue(matchedValues.length ? matchedValues : undefined);
				} else {
					setSelectedValue(undefined);
				}

				setOptions(transformedOptions);
			} else {
				setOptions([]);
			}

			setLoading(false);
		},
		[batchId, functionarySrcmId, regionId, value]
	);

	useEffect(() => {
		if (value?.length && isFilterOpen) {
			void fetchData('');
		} else {
			setSelectedValue(undefined);
		}
	}, [fetchData, value, isFilterOpen]);

	const debouncedFetchData = useMemo(
		() => debounce(fetchData, 500),
		[fetchData]
	);

	const handleSearch = useCallback(
		(searchKey: SelectValue) => {
			void debouncedFetchData(searchKey as string);
		},
		[debouncedFetchData]
	);

	const handleFocus = useCallback(() => {
		if (options.length === 0) {
			void fetchData('');
		}
	}, [fetchData, options.length]);

	return (
		<Select
			showSearch
			mode="multiple"
			defaultActiveFirstOption={false}
			filterOption={false}
			placeholder={t('searchZones')}
			notFoundContent={loading ? <Spin size="small" /> : null}
			onFocus={handleFocus}
			onSearch={handleSearch}
			onChange={onChange}
			labelInValue
			value={selectedValue}
			suffixIcon={null}
			className="font-sans text-sm text-secondary-gray"
			disabled={!regionId}
		>
			{options.map((option) => (
				<Option key={option.value} value={option.value}>
					{option.label}
				</Option>
			))}
		</Select>
	);
};

export default RemoteSingleSelect;
